import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function AdminEditUserForm() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { token, user: adminUser } = useAuth();
    const [userData, setUserData] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await fetch(`https://members.abssvss.com/members_api/get_user_details_admin.php?id=${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message);
                setUserData(data);
            } catch (error) {
                setMessage(`Error: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        fetchUserData();
    }, [userId, token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhotoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        setMessage('Uploading photo...');

        const formData = new FormData();
        formData.append('profilePhoto', file);
        formData.append('userId', userId);

        try {
            const response = await fetch(`https://members.abssvss.com/members_api/update_user_photo_admin.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            
            setUserData(prev => ({ ...prev, profilePictureUrl: result.profilePictureUrl }));
            setMessage('Photo uploaded successfully!');
        } catch (error) {
            setMessage(`Upload Error: ${error.message}`);
        } finally {
            setIsUploading(false);
            if(fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('Saving changes...');
        
        const payload = { ...userData };
        if (newPassword) {
            payload.newPassword = newPassword;
        }

        try {
            const response = await fetch(`https://members.abssvss.com/members_api/update_user_admin.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            setMessage('User updated successfully! Redirecting...');
            setTimeout(() => navigate('/portal-management-7a8b9c/manage-members'), 2000);
        } catch (error) {
            setMessage(`Save Error: ${error.message}`);
        }
    };

    if (isLoading) return <p>Loading user data...</p>;
    if (!userData) return <p className="text-red-500">{message || 'User not found.'}</p>;

    return (
        <div className="w-full">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">Edit Member: {userData.firstName} {userData.lastName}</h2>
            <form onSubmit={handleSubmit} className="space-y-6 bg-gray-50 p-6 rounded-lg">
                <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                        <img 
                            src={userData.profilePictureUrl ? `https://members.abssvss.com${userData.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${userData.firstName}+${userData.lastName}&background=8b0000&color=fff&size=128`}
                            alt="Profile Preview"
                            className="w-32 h-32 rounded-lg object-cover border"
                        />
                    </div>
                    <div className="flex-grow">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Change Profile Photo (JPG/PNG, max 2MB)</label>
                        <input type="file" accept="image/jpeg, image/png" onChange={handlePhotoUpload} ref={fileInputRef} disabled={isUploading} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"/>
                        {isUploading && <p className="text-sm text-blue-600 mt-2">Uploading...</p>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="firstName" value={userData.firstName || ''} onChange={handleChange} placeholder="First Name" className="p-2 border rounded" />
                    <input name="lastName" value={userData.lastName || ''} onChange={handleChange} placeholder="Last Name" className="p-2 border rounded" />
                    <input name="email" type="email" value={userData.email || ''} onChange={handleChange} placeholder="Email" className="p-2 border rounded" />
                    <input name="phoneNumber" value={userData.phoneNumber || ''} onChange={handleChange} placeholder="Phone Number" className="p-2 border rounded" />
                    <input name="state" value={userData.state || ''} onChange={handleChange} placeholder="State" className="p-2 border rounded" />
                    <input name="city" value={userData.city || ''} onChange={handleChange} placeholder="City" className="p-2 border rounded" />
                    <input name="position" value={userData.position || ''} onChange={handleChange} placeholder="Position" className="p-2 border rounded" />
                    <select name="status" value={userData.status || 'inactive'} onChange={handleChange} className="p-2 border rounded">
                        <option value="active">active</option>
                        <option value="pending_approval">pending_approval</option>
                        <option value="pending_edit_approval">pending_edit_approval</option>
                        <option value="rejected">rejected</option>
                        <option value="inactive">inactive</option>
                    </select>
                    {adminUser?.role === 'superadmin' && (
                        <>
                            <select name="role" value={userData.role || 'user'} onChange={handleChange} className="p-2 border rounded">
                                <option value="user">user</option>
                                <option value="admin">admin</option>
                                <option value="superadmin">superadmin</option>
                            </select>
                            {userData.role === 'admin' && (
                                <input name="assignedState" value={userData.assignedState || ''} onChange={handleChange} placeholder="Assigned State for Admin" className="p-2 border rounded" />
                            )}
                        </>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Date of Joining</label>
                        <input 
                            type="date"
                            name="joiningDate" 
                            value={userData.joiningDate ? userData.joiningDate.split(' ')[0] : ''} 
                            onChange={handleChange} 
                            className="p-2 border rounded w-full" 
                        />
                    </div>
                </div>
                
                {adminUser?.role === 'superadmin' && (
                    <div className="border-t pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Set New Password</label>
                        <input 
                            type="password" 
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Leave blank to keep current password" 
                            className="w-full p-2 border rounded" 
                        />
                    </div>
                )}

                <textarea name="bio" value={userData.bio || ''} onChange={handleChange} placeholder="Bio" className="w-full p-2 border rounded" rows="4"></textarea>
                <div className="flex justify-end gap-4">
                     <button type="button" onClick={() => navigate('/portal-management-7a8b9c/manage-members')} className="bg-gray-500 text-white font-bold py-2 px-4 rounded">Cancel</button>
                    <button type="submit" className="bg-brand-primary text-white font-bold py-2 px-4 rounded">Save All Changes</button>
                </div>
                 {message && <p className="text-center mt-4">{message}</p>}
            </form>
        </div>
    );
}

export default AdminEditUserForm;

